import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Play, Ship, Zap, Eye, TrendingUp, Activity, CheckCircle, ArrowRight, DollarSign } from 'lucide-react';

const MaritimeChainDemo = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [voyageProgress, setVoyageProgress] = useState(0);
  const [contractProgress, setContractProgress] = useState(0);
  const [animatingTokens, setAnimatingTokens] = useState(false);
  const [showPayoutFlow, setShowPayoutFlow] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  // Ship components data
  const shipComponents = [
    { name: 'Reinforced Hull', value: 12000000, status: 'Optimal', temp: '22°C', integrity: '99%', color: 'from-blue-500 to-cyan-500' },
    { name: 'Main Engine (Type XJ)', value: 5000000, status: 'Active', temp: '85°C', integrity: '97%', color: 'from-red-500 to-orange-500' },
    { name: 'Navigation & Comms Array', value: 2000000, status: 'Online', temp: '35°C', integrity: '100%', color: 'from-green-500 to-teal-500' },
    { name: 'Auxiliary Power Systems', value: 1000000, status: 'Standby', temp: '28°C', integrity: '98%', color: 'from-purple-500 to-pink-500' }
  ];

  const totalShipValue = shipComponents.reduce((sum, comp) => sum + comp.value, 0);

  // Token holders data
  const tokenHolders = [
    { name: 'Alpha Hedge Fund', assets: '15% of Hull', investment: 1800000, preBalance: 45230, yield: 0, color: 'bg-blue-500' },
    { name: 'Logistics DAO', assets: '8% of Engine, 2% of Hull', investment: 640000, preBalance: 12840, yield: 0, color: 'bg-green-500' },
    { name: 'Maritime Pension Fund', assets: '25% of Hull', investment: 3000000, preBalance: 78650, yield: 0, color: 'bg-purple-500' },
    { name: 'Retail Investor 42', assets: '1% of Navigation', investment: 20000, preBalance: 245, yield: 0, color: 'bg-cyan-500' },
    { name: 'Pacific Capital', assets: '12% of Engine', investment: 600000, preBalance: 15420, yield: 0, color: 'bg-red-500' },
    { name: 'DeFi Yield Farm', assets: '3% of Hull, 5% of Systems', investment: 410000, preBalance: 8930, yield: 0, color: 'bg-yellow-500' },
    { name: 'Singapore Maritime Trust', assets: '20% of Navigation', investment: 400000, preBalance: 9845, yield: 0, color: 'bg-indigo-500' },
    { name: 'Blockchain Ventures', assets: '6% of Engine, 1% of Hull', investment: 420000, preBalance: 10250, yield: 0, color: 'bg-pink-500' }
  ];

  const [holders, setHolders] = useState(tokenHolders);

  const steps = [
    { title: 'Introduction', subtitle: 'Maritime Chain Protocol Overview' },
    { title: 'Asset Tokenization', subtitle: 'Ship Component Breakdown' },
    { title: 'Token Distribution', subtitle: 'Stakeholder Portfolio' },
    { title: 'Voyage Initiation', subtitle: 'Smart Contract Deployment' },
    { title: 'Live Telemetry', subtitle: 'Real-time Monitoring' },
    { title: 'Contract Execution', subtitle: 'Automated Distribution' },
    { title: 'Settlement Report', subtitle: 'Yield Distribution Complete' }
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(currentStep + 1);
    }
  };

  const initiateVoyage = () => {
    setVoyageProgress(0);
    const progressInterval = setInterval(() => {
      setVoyageProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => nextStep(), 1500);
          return 100;
        }
        return prev + 1.5;
      });
    }, 50);
  };

  const executeContract = () => {
    setContractProgress(0);
    setShowPayoutFlow(true);
    
    const contractInterval = setInterval(() => {
      setContractProgress(prev => {
        if (prev >= 100) {
          clearInterval(contractInterval);
          setAnimatingTokens(true);
          calculateYields();
          setTimeout(() => nextStep(), 2000);
          return 100;
        }
        return prev + 2;
      });
    }, 80);
  };

  const calculateYields = () => {
    const totalYield = 25000;
    const totalInvestment = holders.reduce((sum, h) => sum + h.investment, 0);
    
    const updatedHolders = holders.map(holder => ({
      ...holder,
      yield: Math.round((holder.investment / totalInvestment) * totalYield * 100) / 100
    }));
    
    setHolders(updatedHolders);
  };

  const resetDemo = () => {
    setCurrentStep(0);
    setVoyageProgress(0);
    setContractProgress(0);
    setAnimatingTokens(false);
    setShowPayoutFlow(false);
    setCompletedSteps(new Set());
    setHolders(tokenHolders);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
      {/* Animated background */}
      <div className="fixed inset-0 bg-gradient-to-br from-blue-900/20 via-gray-900 to-cyan-900/20">
        <div className="absolute inset-0">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-cyan-400/10 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 3 + 1}px`,
                height: `${Math.random() * 3 + 1}px`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${Math.random() * 2 + 2}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gray-800/50 backdrop-blur-sm">
        <div className="h-1 bg-gray-700">
          <div 
            className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-500 ease-out"
            style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
          />
        </div>
        <div className="px-6 py-4 flex justify-between items-center">
          <div>
            <h3 className="font-mono text-lg text-cyan-400">STEP {currentStep + 1}</h3>
            <p className="text-gray-400">{steps[currentStep]?.title}</p>
          </div>
          <div className="flex space-x-2">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  completedSteps.has(idx) 
                    ? 'bg-green-400 scale-110' 
                    : currentStep === idx 
                      ? 'bg-cyan-400 animate-pulse' 
                      : 'bg-gray-600'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="relative z-10 pt-20">
        {/* Step 0: Introduction */}
        {currentStep === 0 && (
          <div className="min-h-screen flex items-center justify-center px-8 animate-fade-in">
            <div className="max-w-4xl mx-auto text-center">
              <div className="mb-8 animate-float">
                <Ship className="w-24 h-24 mx-auto mb-6 text-cyan-400" />
                <div className="text-8xl font-mono font-bold mb-4 bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent animate-gradient">
                  MARITIME CHAIN
                </div>
                <div className="text-3xl font-mono mb-6 text-gray-300 animate-slide-up" style={{animationDelay: '0.5s'}}>
                  Modular Asset Tokenization Protocol
                </div>
                <p className="text-xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed animate-slide-up" style={{animationDelay: '1s'}}>
                  Fractional ownership of critical maritime infrastructure with automated revenue cascading.
                  Experience how cargo ship "Ocean Star" components are tokenized and generate yield for diverse investors.
                </p>
              </div>
              
              <Button 
                onClick={nextStep}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-black font-mono text-xl px-12 py-6 rounded-none transform hover:scale-105 transition-all duration-300 animate-bounce-in"
                style={{animationDelay: '1.5s'}}
              >
                BEGIN TOKENIZATION DEMO
                <ArrowRight className="ml-3 w-6 h-6" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 1: Asset Tokenization */}
        {currentStep === 1 && (
          <div className="min-h-screen py-20 px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12 animate-slide-down">
                <h2 className="text-5xl font-mono font-bold mb-4 text-cyan-400">
                  OCEAN STAR TOKENIZATION
                </h2>
                <p className="text-xl text-gray-400">Breaking down $20M vessel into tradeable components</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8 mb-12">
                {shipComponents.map((component, idx) => (
                  <Card 
                    key={idx} 
                    className="bg-gray-800/50 backdrop-blur-sm border border-cyan-500/20 transform hover:scale-105 transition-all duration-500 animate-slide-in"
                    style={{animationDelay: `${idx * 0.2}s`}}
                  >
                    <CardContent className="p-6">
                      <div className={`w-full h-4 bg-gradient-to-r ${component.color} rounded mb-4 animate-pulse`} />
                      <h3 className="font-mono text-2xl text-white mb-2">{component.name}</h3>
                      <div className="text-3xl font-bold text-cyan-400 mb-4">
                        ${component.value.toLocaleString()}
                      </div>
                      <div className="flex justify-between text-sm text-gray-400">
                        <span>Status: <span className="text-green-400">{component.status}</span></span>
                        <span>Integrity: {component.integrity}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="bg-gradient-to-r from-cyan-900/50 to-blue-900/50 backdrop-blur-sm border border-cyan-500 animate-glow">
                <CardContent className="p-8 text-center">
                  <div className="text-4xl font-mono font-bold text-cyan-400 mb-2">
                    TOTAL VESSEL VALUE
                  </div>
                  <div className="text-6xl font-mono font-bold text-white animate-count-up">
                    ${totalShipValue.toLocaleString()}
                  </div>
                  <p className="text-gray-300 mt-4">Ready for fractional ownership distribution</p>
                </CardContent>
              </Card>

              <div className="text-center mt-12">
                <Button 
                  onClick={nextStep}
                  className="bg-gradient-to-r from-green-500 to-cyan-500 hover:from-green-400 hover:to-cyan-400 text-black font-mono text-xl px-10 py-4 rounded-none transform hover:scale-105 transition-all duration-300"
                >
                  VIEW TOKEN DISTRIBUTION
                  <ArrowRight className="ml-3 w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Token Distribution */}
        {currentStep === 2 && (
          <div className="min-h-screen py-20 px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12 animate-slide-down">
                <h2 className="text-5xl font-mono font-bold mb-4 text-cyan-400">
                  ACTIVE TOKEN HOLDERS
                </h2>
                <p className="text-xl text-gray-400">Distributed ownership across institutional and retail investors</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-12">
                {holders.map((holder, idx) => (
                  <Card 
                    key={idx}
                    className="bg-gray-800/50 backdrop-blur-sm border border-gray-600/20 transform hover:scale-105 transition-all duration-500 animate-slide-in"
                    style={{animationDelay: `${idx * 0.1}s`}}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center mb-3">
                        <div className={`w-4 h-4 rounded-full ${holder.color} mr-3 animate-pulse`} />
                        <h3 className="font-mono text-lg text-white">{holder.name}</h3>
                      </div>
                      <p className="text-gray-400 mb-2">{holder.assets}</p>
                      <div className="flex justify-between">
                        <span className="text-cyan-400 font-mono">
                          ${holder.investment.toLocaleString()}
                        </span>
                        <span className="text-green-400 font-mono">
                          Balance: ${holder.preBalance.toFixed(2)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="text-center">
                <Button 
                  onClick={nextStep}
                  className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-400 hover:to-purple-400 text-white font-mono text-xl px-10 py-4 rounded-none transform hover:scale-105 transition-all duration-300"
                >
                  INITIATE SMART CONTRACT
                  <Zap className="ml-3 w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Voyage Initiation */}
        {currentStep === 3 && (
          <div className="min-h-screen py-20 px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="animate-slide-down mb-12">
                <h2 className="text-5xl font-mono font-bold mb-6 text-cyan-400">
                  FREIGHT CONTRACT DEPLOYED
                </h2>
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-8 border border-cyan-500/20 mb-8">
                  <div className="grid md:grid-cols-3 gap-6 text-center">
                    <div>
                      <div className="text-2xl font-bold text-white mb-2">SINGAPORE → ROTTERDAM</div>
                      <div className="text-gray-400">Route</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-400 mb-2">$15,000,000</div>
                      <div className="text-gray-400">Cargo Value</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-cyan-400 mb-2">$250,000</div>
                      <div className="text-gray-400">Freight Fee</div>
                    </div>
                  </div>
                </div>
              </div>

              <Card className="bg-gray-800/50 backdrop-blur-sm border border-cyan-500/20 mb-8 animate-glow">
                <CardContent className="p-12">
                  <Ship className="w-32 h-32 mx-auto mb-6 text-cyan-400 animate-float" />
                  <h3 className="text-3xl font-mono font-bold text-white mb-4">OCEAN STAR</h3>
                  <p className="text-xl text-gray-400 mb-8">Ready for autonomous voyage execution</p>
                  <div className="text-lg text-cyan-400 font-mono mb-8 animate-pulse">
                    Smart Contract Status: DEPLOYED & ACTIVE
                  </div>
                  
                  <Button 
                    onClick={() => { nextStep(); initiateVoyage(); }}
                    className="bg-gradient-to-r from-green-500 to-cyan-500 hover:from-green-400 hover:to-cyan-400 text-black font-mono text-2xl px-12 py-6 rounded-none transform hover:scale-110 transition-all duration-300 animate-bounce-in"
                  >
                    <Play className="mr-4 w-8 h-8" />
                    LAUNCH VOYAGE
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 4: Live Telemetry */}
        {currentStep === 4 && (
          <div className="min-h-screen py-20 px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8 animate-slide-down">
                <h2 className="text-5xl font-mono font-bold mb-4 text-cyan-400">
                  LIVE TELEMETRY FEED
                </h2>
                <p className="text-xl text-gray-400">Real-time monitoring of vessel systems</p>
              </div>

              <Card className="bg-gray-800/50 backdrop-blur-sm border border-cyan-500/20 mb-8">
                <CardContent className="p-8">
                  <div className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                      <span className="font-mono text-2xl text-cyan-400">VOYAGE PROGRESS</span>
                      <span className="font-mono text-3xl text-white">{voyageProgress.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-700 h-6 rounded-lg overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-200 relative"
                        style={{ width: `${voyageProgress}%` }}
                      >
                        <div className="absolute inset-0 bg-white/20 animate-pulse" />
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {shipComponents.map((component, idx) => (
                      <div key={idx} className={`bg-gray-900/50 p-6 rounded-lg border border-gray-700 ${voyageProgress > 20 ? 'animate-pulse' : ''}`}>
                        <div className="flex justify-between items-center mb-4">
                          <span className="font-mono text-xl text-white">{component.name}</span>
                          <Activity className={`w-6 h-6 ${voyageProgress > 0 ? 'text-green-400 animate-bounce' : 'text-gray-500'}`} />
                        </div>
                        <div className={`w-full h-3 bg-gradient-to-r ${component.color} rounded mb-3`}>
                          <div className="h-full bg-white/30 animate-pulse" />
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-sm font-mono">
                          <div>
                            <span className="text-green-400">Status:</span>
                            <div className="text-white">{component.status}</div>
                          </div>
                          <div>
                            <span className="text-cyan-400">Temp:</span>
                            <div className="text-white">{component.temp}</div>
                          </div>
                          <div>
                            <span className="text-blue-400">Integrity:</span>
                            <div className="text-white">{component.integrity}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {voyageProgress === 100 && (
                    <div className="mt-8 p-6 bg-gradient-to-r from-green-900/50 to-cyan-900/50 border border-green-500 rounded-lg text-center animate-slide-in">
                      <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400 animate-bounce" />
                      <div className="text-2xl font-mono font-bold text-green-400 mb-2">
                        VOYAGE COMPLETED SUCCESSFULLY
                      </div>
                      <div className="text-lg font-mono text-gray-300 mb-4">
                        Oracle verified: Cargo delivered to Rotterdam Port
                      </div>
                      <Button 
                        onClick={() => { nextStep(); executeContract(); }}
                        className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black font-mono text-xl px-8 py-4 rounded-none transform hover:scale-105 transition-all duration-300"
                      >
                        EXECUTE PAYMENT CONTRACT
                        <Zap className="ml-3 w-5 h-5" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 5: Contract Execution */}
        {currentStep === 5 && (
          <div className="min-h-screen py-20 px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12 animate-slide-down">
                <h2 className="text-5xl font-mono font-bold mb-4 text-cyan-400">
                  SMART CONTRACT EXECUTION
                </h2>
                <p className="text-xl text-gray-400">Automated revenue distribution in progress</p>
              </div>

              <div className="relative">
                {/* Central Processing Node */}
                <div className="flex flex-col items-center space-y-12">
                  <div className={`relative ${contractProgress > 0 ? 'animate-spin-slow' : ''}`}>
                    <div className="bg-gradient-to-r from-green-500 to-cyan-500 w-48 h-48 rounded-full flex items-center justify-center border-4 border-white/20">
                      <div className="text-center font-mono">
                        <DollarSign className="w-12 h-12 mx-auto mb-2 text-black" />
                        <div className="text-xl font-bold text-black">$250,000</div>
                        <div className="text-sm text-black">FREIGHT FEE</div>
                      </div>
                    </div>
                    {contractProgress > 0 && (
                      <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-cyan-500 rounded-full animate-ping opacity-20" />
                    )}
                  </div>

                  {/* Progress indicator */}
                  <Card className="bg-gray-800/50 backdrop-blur-sm border border-cyan-500/20 w-full max-w-2xl">
                    <CardContent className="p-8">
                      <div className="text-center mb-6">
                        <Zap className="w-16 h-16 mx-auto mb-4 text-cyan-400" />
                        <div className="font-mono text-2xl text-cyan-400">CONTRACT PROCESSOR</div>
                      </div>
                      
                      <div className="w-full bg-gray-700 h-4 rounded mb-6">
                        <div 
                          className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded transition-all duration-200"
                          style={{ width: `${contractProgress}%` }}
                        />
                      </div>

                      {contractProgress > 0 && (
                        <div className="bg-black/70 p-4 rounded font-mono text-sm text-green-400 overflow-hidden h-32">
                          <div className="animate-scroll-up">
                            <div className="mb-1">{'> Initializing payment distribution...'}</div>
                            <div className="mb-1">{'> Validating token holder addresses...'}</div>
                            <div className="mb-1">{'> Calculating proportional yields...'}</div>
                            <div className="mb-1">{'> Processing 90% to ship operator ($225,000)...'}</div>
                            <div className="mb-1">{'> Allocating 10% to token yield pool ($25,000)...'}</div>
                            <div className="mb-1">{'> Executing micro-distribution to 8 holders...'}</div>
                            <div className="mb-1">{'> Broadcasting to blockchain network...'}</div>
                            <div className="mb-1 text-cyan-400">{'> Distribution complete - Gas used: 21,000'}</div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Distribution visualization */}
                  {showPayoutFlow && (
                    <div className="grid md:grid-cols-2 gap-8 w-full max-w-4xl animate-slide-in">
                      <Card className="bg-gray-800/50 backdrop-blur-sm border border-gray-500/20 transform hover:scale-105 transition-all duration-500">
                        <CardContent className="p-8 text-center">
                          <div className="text-2xl font-mono text-gray-400 mb-4">SHIP OPERATOR</div>
                          <div className="text-5xl font-mono font-bold text-white mb-2">$225,000</div>
                          <div className="text-lg text-gray-500">90% Operations Fee</div>
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-gray-800/50 backdrop-blur-sm border border-cyan-500/20 transform hover:scale-105 transition-all duration-500 animate-glow">
                        <CardContent className="p-8 text-center">
                          <div className="text-2xl font-mono text-cyan-400 mb-4">TOKEN YIELD POOL</div>
                          <div className="text-5xl font-mono font-bold text-cyan-400 mb-2">$25,000</div>
                          <div className="text-lg text-gray-400">10% to {holders.length} Holders</div>
                          {animatingTokens && (
                            <div className="mt-4 flex justify-center space-x-1">
                              {[...Array(8)].map((_, i) => (
                                <div
                                  key={i}
                                  className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce"
                                  style={{ animationDelay: `${i * 0.1}s` }}
                                />
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 6: Settlement Report */}
        {currentStep === 6 && (
          <div className="min-h-screen py-20 px-8">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12 animate-slide-down">
                <h2 className="text-5xl font-mono font-bold mb-4 text-cyan-400">
                  SETTLEMENT REPORT
                </h2>
                <p className="text-xl text-gray-400">Automated yield distribution completed</p>
              </div>

              <Card className="bg-gray-800/50 backdrop-blur-sm border border-cyan-500/20 mb-8 animate-slide-in">
                <CardContent className="p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full font-mono text-sm">
                      <thead>
                        <tr className="border-b border-gray-700">
                          <th className="text-left py-4 text-cyan-400 text-lg">TOKEN HOLDER</th>
                          <th className="text-left py-4 text-cyan-400 text-lg">ASSETS</th>
                          <th className="text-right py-4 text-cyan-400 text-lg">PRE-VOYAGE</th>
                          <th className="text-right py-4 text-cyan-400 text-lg">YIELD EARNED</th>
                          <th className="text-right py-4 text-green-400 text-lg">NEW BALANCE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {holders.map((holder, idx) => (
                          <tr 
                            key={idx} 
                            className="border-b border-gray-800 hover:bg-gray-700/30 transition-all duration-300 animate-slide-in"
                            style={{animationDelay: `${idx * 0.1}s`}}
                          >
                            <td className="py-4">
                              <div className="flex items-center">
                                <div className={`w-3 h-3 rounded-full ${holder.color} mr-3 animate-pulse`} />
                                <span className="text-white text-lg">{holder.name}</span>
                              </div>
                            </td>
                            <td className="py-4 text-gray-400">{holder.assets}</td>
                            <td className="py-4 text-right text-gray-300 text-lg">${holder.preBalance.toFixed(2)}</td>
                            <td className="py-4 text-right text-cyan-400 text-lg font-bold">+${holder.yield.toFixed(2)}</td>
                            <td className="py-4 text-right text-green-400 text-xl font-bold">
                              ${(holder.preBalance + holder.yield).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Footer Stats */}
              <div className="grid md:grid-cols-4 gap-6 mb-12">
                <Card className="bg-gray-800/50 backdrop-blur-sm border border-green-500/20 text-center animate-bounce-in">
                  <CardContent className="p-6">
                    <div className="text-4xl font-mono font-bold text-green-400 mb-2">$25,000</div>
                    <div className="text-gray-400">Total Distributed</div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gray-800/50 backdrop-blur-sm border border-cyan-500/20 text-center animate-bounce-in" style={{animationDelay: '0.2s'}}>
                  <CardContent className="p-6">
                    <div className="text-4xl font-mono font-bold text-cyan-400 mb-2">$1.42</div>
                    <div className="text-gray-400">Gas Fees</div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gray-800/50 backdrop-blur-sm border border-blue-500/20 text-center animate-bounce-in" style={{animationDelay: '0.4s'}}>
                  <CardContent className="p-6">
                    <div className="text-4xl font-mono font-bold text-blue-400 mb-2">400ms</div>
                    <div className="text-gray-400">Execution Time</div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gray-800/50 backdrop-blur-sm border border-purple-500/20 text-center animate-bounce-in" style={{animationDelay: '0.6s'}}>
                  <CardContent className="p-6">
                    <div className="text-4xl font-mono font-bold text-purple-400 mb-2">0</div>
                    <div className="text-gray-400">Manual Paperwork</div>
                  </CardContent>
                </Card>
              </div>

              <div className="text-center">
                <Button 
                  onClick={resetDemo}
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-400 hover:to-cyan-400 text-black font-mono text-2xl px-12 py-6 rounded-none transform hover:scale-110 transition-all duration-300 animate-pulse"
                >
                  RESTART DEMONSTRATION
                  <ArrowRight className="ml-4 w-6 h-6" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-down {
          from { opacity: 0; transform: translateY(-30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes bounce-in {
          0% { opacity: 0; transform: scale(0.3); }
          50% { transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(6, 182, 212, 0.3); }
          50% { box-shadow: 0 0 30px rgba(6, 182, 212, 0.6), 0 0 40px rgba(6, 182, 212, 0.3); }
        }
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes count-up {
          from { transform: scale(0.5); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes scroll-up {
          0% { transform: translateY(20px); }
          100% { transform: translateY(-20px); }
        }
        
        .animate-fade-in { animation: fade-in 1s ease-out forwards; }
        .animate-slide-up { animation: slide-up 0.8s ease-out forwards; opacity: 0; }
        .animate-slide-down { animation: slide-down 0.8s ease-out forwards; opacity: 0; }
        .animate-slide-in { animation: slide-in 0.8s ease-out forwards; opacity: 0; }
        .animate-bounce-in { animation: bounce-in 0.8s ease-out forwards; opacity: 0; }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .animate-glow { animation: glow 2s ease-in-out infinite; }
        .animate-gradient { background-size: 400% 400%; animation: gradient 3s ease infinite; }
        .animate-count-up { animation: count-up 1s ease-out forwards; }
        .animate-spin-slow { animation: spin-slow 3s linear infinite; }
        .animate-scroll-up { animation: scroll-up 2s linear infinite; }
      `}</style>
    </div>
  );
};

export default MaritimeChainDemo;